import { useMemo } from 'react';

import { useFillPrescription } from '../../../../contexts/fill/use-fill-prescription';
import type { PrescriptionOffer } from '../../../../hooks/use-prescription-offers';
import type { PricingCardElementProps } from '../common/shared';

export type UseSelfPayPricing = {
    patientPay: number | null;
    priceType: PrescriptionOffer['headlinePriceType'];
    savings: number | null;
};

export const useSelfPayPricing = (
    offer: PricingCardElementProps['offer']
): UseSelfPayPricing => {
    const { allOffers } = useFillPrescription();

    const cashOffer = allOffers.find((o) => o.headlinePriceType === 'Cash');
    const incentiveOffer = allOffers.find(
        (o) => o.headlinePriceType === 'Incentive'
    );
    const pharmacyIncentiveOffer = allOffers.find(
        (o) => o.headlinePriceType === 'PharmacyIncentive' && o.id === offer?.id
    );
    const cashAssistanceOffer = allOffers.find(
        (o) => o.headlinePriceType === 'CashAssistance'
    );

    const { patientPay, priceType, savings } = useMemo(() => {
        let patientPay: number | null = null,
            priceType: PrescriptionOffer['headlinePriceType'] = null;

        if (offer && 'patientCost' in offer && offer?.patientCost) {
            patientPay = offer.patientCost;
            priceType = offer.headlinePriceType;
        } else if (pharmacyIncentiveOffer?.patientCost) {
            patientPay = pharmacyIncentiveOffer.patientCost;
            priceType = pharmacyIncentiveOffer.headlinePriceType;
        } else if (incentiveOffer?.patientCost) {
            patientPay = incentiveOffer.patientCost;
            priceType = incentiveOffer.headlinePriceType;
        } else if (cashAssistanceOffer?.patientCost) {
            patientPay = cashAssistanceOffer.patientCost;
            priceType = cashAssistanceOffer.headlinePriceType;
        } else if (cashOffer?.patientCost) {
            patientPay = cashOffer.patientCost;
            priceType = cashOffer.headlinePriceType;
        }

        const savings =
            cashOffer?.patientCost != null &&
            patientPay !== null &&
            (priceType === 'Incentive' || priceType === 'PharmacyIncentive')
                ? cashOffer.patientCost - patientPay
                : null;

        return { patientPay, priceType, savings };
    }, [
        cashAssistanceOffer,
        cashOffer,
        incentiveOffer,
        offer,
        pharmacyIncentiveOffer,
    ]);

    return {
        patientPay,
        priceType,
        savings,
    };
};
